import { configureStore, combineReducers, Reducer, AnyAction, UnknownAction } from "@reduxjs/toolkit";

// Core reducers needed for auth
import LayoutReducer from "./slices/layouts/reducer";
import Co2AuthSlice from './slices/co2auth/reducer';

// Create a type for dynamic reducers
interface DynamicReducers {
    [key: string]: Reducer<any, any>;
}

// Initial minimal reducer configuration for auth pages
const createAuthReducer = () => combineReducers({
    Layout: LayoutReducer,
    Co2Auth: Co2AuthSlice,
});

// Store reference to allow dynamic reducer injection
let currentReducers: DynamicReducers = {
    Layout: LayoutReducer,
    Co2Auth: Co2AuthSlice,
};

// Create store
export const store = configureStore({ 
    reducer: createAuthReducer(), 
    devTools: true 
});

// Function to inject reducers dynamically
export const injectReducer = (key: string, reducer: Reducer<any, any>) => {
    if (currentReducers[key]) return; // Already loaded
    
    currentReducers[key] = reducer;
    store.replaceReducer(combineReducers(currentReducers) as any);
};

// Function to load all app reducers (called after auth)
export const loadAppReducers = async () => {
    const [
        Co2MenuSlice,
        Co2ProfileSlice,
        Co2AdminSlice,
        Co2BusinessSlice,
        Co2AffiliateSlice,
        Co2NotificationsSlice,
        Co2SpeechSlice,
        Co2TokensSlice,
    ] = await Promise.all([
        import('./slices/co2menu/reducer').then(m => m.default),
        import('./slices/co2profile/reducer').then(m => m.default),
        import('./slices/co2admin/reducer').then(m => m.default),
        import('./slices/co2business/reducer').then(m => m.default),
        import('./slices/co2affiliate/reducer').then(m => m.default),
        import('./slices/co2notifications/reducer').then(m => m.default),
        import('./slices/co2speech/reducer').then(m => m.default),
        import('./slices/co2tokens/reducer').then(m => m.default),
    ]);

    const allReducers = {
        ...currentReducers,
        Co2Menu: Co2MenuSlice,
        Co2Profile: Co2ProfileSlice,
        Co2Admin: Co2AdminSlice,
        Co2Business: Co2BusinessSlice,
        Co2Affiliate: Co2AffiliateSlice,
        Co2Notifications: Co2NotificationsSlice,
        Co2Speech: Co2SpeechSlice,
        Co2Tokens: Co2TokensSlice,
    };

    currentReducers = allReducers;
    store.replaceReducer(combineReducers(allReducers) as any);
};

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
